import React, {useEffect, useState} from 'react'
import {
  LocationDetailsMap,
  useCurrentUserAnPCheck,
  useLocationContext,
  useWatchGeolocation,
} from '@therms/react-modules'
import {Button, Icon} from '@therms/atalaya'
import {checkLatLngWithinLocationGeo} from '@src/modules/Locations/utils/checkLatLngWithinLocationGeo'
import dayjs from 'dayjs'
import {MdGpsFixed, MdGpsOff} from 'react-icons/md'

interface VerifyUserGeoForLocationLogsProps {
  onVerified: () => void
}

function VerifyUserGeoForLocationLogs({
  onVerified,
}: VerifyUserGeoForLocationLogsProps) {
  const canBypass = useCurrentUserAnPCheck(
    ({anp}) => anp.role.ops.permissions.canBypassRequiredGPS,
  )
  const {location} = useLocationContext(true)
  const geo = useWatchGeolocation()

  const check = () => {
    if (!location.geo) return true

    if (geo.accuracy) {
      return checkLatLngWithinLocationGeo(
        {
          accuracy: geo.accuracy,
          lat: geo.lat,
          lng: geo.lng,
        },
        location.geo,
      )
    }

    if (
      geo.previous &&
      dayjs(geo.previous.timestamp).isBefore(dayjs().subtract(5, 'minute'))
    ) {
      return checkLatLngWithinLocationGeo(
        {
          accuracy: geo.previous.accuracy,
          lat: geo.previous.lat,
          lng: geo.previous.lng,
        },
        location.geo,
      )
    }

    return false
  }

  const [failed, setFailed] = useState<string | undefined>()

  useEffect(() => {
    if (geo.error?.message) {
      setFailed(geo.error.message)
    }

    if (!check()) {
      setFailed('Device Not Within Location Perimeter')
    } else {
      onVerified()
    }
  }, [geo])

  if (geo.loading) {
    return (
      <div>
        <div className="text-center font-bold">
          Verifying Device GPS for Log Entry Permission
        </div>

        <div className="text-color-neutral text-center">
          You are restricted from logging entries at this Location unless you
          are within the Location's perimeter.
        </div>

        <div className="flex justify-center">
          <div className="mt-3 h-10 w-10">
            <div className="bg-neutral-faded flex h-full w-full animate-pulse items-center justify-center rounded-full">
              <Icon color="main" Icon={MdGpsFixed} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (failed) {
    return (
      <div className="text-center">
        <div>
          <div className="text-critical font-bold">
            GPS Check Failed - {failed}
          </div>

          {canBypass && (
            <div className="my-base">
              <Button onClick={onVerified} variant="caution">
                <Icon Icon={MdGpsOff} /> Bypass GPS Check
              </Button>
            </div>
          )}

          {geo.accuracy && (
            <div className="mt-base">
              <div className="text-color-info">
                Device GPS Accuracy: {geo.accuracy}m
              </div>

              <LocationDetailsMap mapHeight={300} />
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}

export {VerifyUserGeoForLocationLogs}
